import { useState, useEffect, useRef } from "react";
import { useParams, Link, useLocation } from "wouter";
import { useGetJobById, useUnlockLead } from "@workspace/api-client-react";
import { useSEO } from "@/hooks/use-seo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Phone, Mail, Briefcase } from "lucide-react";
import { GoogleAd } from "@/components/GoogleAd";

const URGENCY_CONFIG: Record<string, { label: string; colour: string }> = {
  emergency: { label: "Emergency", colour: "bg-red-100 text-red-700 border-red-200" },
  within_24h: { label: "Within 24 hours", colour: "bg-orange-100 text-orange-700 border-orange-200" },
  this_week: { label: "This week", colour: "bg-amber-100 text-amber-700 border-amber-200" },
  this_month: { label: "This month", colour: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  flexible: { label: "Flexible", colour: "bg-green-100 text-green-700 border-green-200" },
};

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [unlocking, setUnlocking] = useState(false);
  const { data, isLoading, refetch } = useGetJobById(Number(id));
  const { mutateAsync: unlockLead } = useUnlockLead();
  const autoUnlockAttempted = useRef(false);

  const isResident = user?.role === "resident";
  const isTrade = user?.role === "tradesperson";
  const isMyJob = isResident && data && (data as { userId?: number }).userId === user?.id;

  useSEO({
    title: data ? `${data.title} — ${data.category} quote request in ${data.town}` : undefined,
    description: data ? `${data.description} · ${data.town}, Ayrshire` : undefined,
    jsonLd: data ? {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": data.title,
      "description": data.description,
      "areaServed": {
        "@type": "City",
        "name": data.town,
      },
      "url": `https://www.ayrshirehub.co.uk/jobs/${data.id}`,
    } : undefined,
  });

  useEffect(() => {
    if (autoUnlockAttempted.current || !user || !id) return;
    const params = new URLSearchParams(window.location.search);
    const paymentSuccess = params.get("payment_success");
    const sessionId = params.get("session_id");
    if (paymentSuccess !== "1" || !sessionId) return;

    autoUnlockAttempted.current = true;
    window.history.replaceState({}, "", window.location.pathname);

    void (async () => {
      setUnlocking(true);
      try {
        await unlockLead({ id: Number(id), data: { stripeSessionId: sessionId } });
        await refetch();
        toast({ title: "Lead Unlocked!", description: "You can now see the contact details for this job. A conversation has been started for you." });
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } };
        toast({ title: "Error", description: err?.response?.data?.error ?? "Could not unlock lead after payment.", variant: "destructive" });
      } finally {
        setUnlocking(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const handleUnlock = async () => {
    if (!user) {
      toast({ title: "Sign in required", description: "You need to sign in as a tradesperson to unlock leads.", variant: "destructive" });
      return;
    }
    if (!isTrade) {
      toast({ title: "Tradesperson account required", description: "Only tradespeople can unlock quote requests.", variant: "destructive" });
      return;
    }
    const leadPrice = data?.leadPrice ?? 0;
    if (leadPrice === 0) {
      if (!confirm("Unlock this lead for free? You'll be able to see the resident's contact details.")) return;
      setUnlocking(true);
      try {
        await unlockLead({ id: Number(id), data: {} });
        await refetch();
        toast({ title: "Lead Unlocked!", description: "You can now see the contact details for this job." });
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } }; message?: string };
        toast({ title: "Error", description: err?.response?.data?.error ?? err?.message ?? "Could not unlock lead.", variant: "destructive" });
      } finally {
        setUnlocking(false);
      }
      return;
    }
    if (!confirm(`This will charge £${leadPrice} to unlock the contact details for this job lead. Continue?`)) return;
    setUnlocking(true);
    try {
      const successUrl = `${window.location.origin}/jobs/${id}`;
      const cancelUrl = window.location.href;
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          amount: leadPrice,
          description: `Job Lead Unlock — ${data?.title ?? "Job"}`,
          successUrl,
          cancelUrl,
          metadata: { type: "lead", jobId: String(id) },
        }),
      });
      const checkout = await res.json() as { url?: string; error?: string };
      if (checkout.url) {
        window.location.href = checkout.url;
        return;
      }
      throw new Error(checkout.error ?? "Could not create payment session");
    } catch (e: unknown) {
      const err = e as Error;
      toast({ title: "Payment error", description: err.message ?? "Could not start payment. Please try again.", variant: "destructive" });
      setUnlocking(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="space-y-4">
          <div className="h-6 bg-slate-200 rounded animate-pulse w-1/4" />
          <div className="h-8 bg-slate-200 rounded animate-pulse w-3/4" />
          <div className="h-32 bg-slate-200 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-4 py-24 text-center">
        <p className="text-5xl mb-4">🔧</p>
        <p className="text-slate-500 font-medium">Job not found.</p>
        <Link href="/jobs"><Button variant="outline" className="mt-4">Back to Quotes</Button></Link>
      </div>
    );
  }

  const date = new Date(data.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const urgency = URGENCY_CONFIG[data.urgency ?? ""] ?? null;
  const conversationId = (data as { conversationId?: number | null }).conversationId;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm text-slate-400 flex-1 min-w-0">
            <Link href="/" className="hover:text-primary transition-colors whitespace-nowrap">Home</Link>
            <span aria-hidden="true">/</span>
            <Link href="/jobs" className="hover:text-primary transition-colors whitespace-nowrap">Get Quotes</Link>
            <span aria-hidden="true">/</span>
            <span className="text-slate-600 truncate" aria-current="page">{data.title}</span>
          </nav>
          {isMyJob && (
            <button
              onClick={() => setLocation("/my-jobs")}
              className="inline-flex items-center gap-1.5 text-xs text-primary font-semibold bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-xl transition-colors"
            >
              <Briefcase size={13} />
              My Quotes
            </button>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8 space-y-6">
          <div>
            <div className="flex flex-wrap gap-2 mb-3">
              {urgency && (
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${urgency.colour}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                  {urgency.label}
                </span>
              )}
              {data.category && (
                <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full">{data.category}</span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-slate-800 leading-snug mb-3">{data.title}</h1>
          </div>

          <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400 w-5 text-center">📍</span>
              <span className="text-slate-500 font-medium w-20">Location</span>
              <span className="text-slate-800">{data.town}</span>
            </div>
            {data.budgetRange && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400 w-5 text-center">💷</span>
                <span className="text-slate-500 font-medium w-20">Budget</span>
                <span className="text-slate-800 font-semibold text-primary">{data.budgetRange}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400 w-5 text-center">📅</span>
              <span className="text-slate-500 font-medium w-20">Posted</span>
              <span className="text-slate-800">{date}</span>
            </div>
          </div>

          <div>
            <h2 className="font-semibold text-slate-800 mb-2">Description</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{data.description}</p>
          </div>

          <div className="my-2">
            <GoogleAd />
          </div>

          {isMyJob && (
            <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
              <p className="text-sm font-semibold text-primary mb-1">This is your quote request</p>
              <p className="text-xs text-slate-500">Tradespeople who unlock this lead will be able to contact you. You'll be notified in the app.</p>
              <button
                onClick={() => setLocation("/my-jobs")}
                className="mt-3 text-xs font-bold text-primary underline"
              >
                View interest in My Quotes →
              </button>
            </div>
          )}

          {!isMyJob && (
            <div className={`rounded-xl border p-5 ${data.unlockedByMe ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
              {data.unlockedByMe ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                    ✅ Lead Unlocked — Contact Details
                  </div>
                  {data.contactName && (
                    <div className="flex items-center gap-2 text-sm">
                      <span>👤</span><span className="text-slate-600">{data.contactName}</span>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3">
                    {data.contactPhone && (
                      <a
                        href={`tel:${data.contactPhone}`}
                        className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-colors"
                      >
                        <Phone size={14} /> Call {data.contactPhone}
                      </a>
                    )}
                    {data.contactEmail && (
                      <a
                        href={`mailto:${data.contactEmail}`}
                        className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-colors"
                      >
                        <Mail size={14} /> Email
                      </a>
                    )}
                    {conversationId ? (
                      <button
                        onClick={() => setLocation(`/messages?conversationId=${conversationId}`)}
                        className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition-colors"
                      >
                        <MessageSquare size={14} /> Message in App
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🔒</span>
                    <div>
                      <p className="font-semibold text-slate-700">Contact details are hidden</p>
                      <p className="text-sm text-slate-500">Unlock this lead to see the resident's contact details and submit your quote.</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleUnlock}
                    disabled={unlocking || !isTrade}
                    className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold"
                  >
                    {unlocking ? "Processing…" : (data.leadPrice ?? 0) === 0 ? "🔓 Unlock Lead for Free" : `🔓 Unlock Lead for £${data.leadPrice}`}
                  </Button>
                  {!user && (
                    <p className="text-xs text-center text-slate-400">
                      <Link href="/login" className="text-primary hover:underline font-semibold">Sign in</Link> or{" "}
                      <Link href={`/signup?role=tradesperson`} className="text-primary hover:underline font-semibold">create a trade account</Link> to unlock leads.
                    </p>
                  )}
                  {user && !isTrade && (
                    <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 space-y-2">
                      <p className="text-sm font-semibold text-primary">Tradesperson account required</p>
                      <p className="text-xs text-slate-500 leading-relaxed">
                        Register as a tradesperson to unlock contact details and win jobs from local residents.
                      </p>
                      <button
                        onClick={() => setLocation(`/become-tradesperson?redirect=/jobs/${id}`)}
                        className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors"
                      >
                        Register as Tradesperson →
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
